import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  TextField,
  FormHelperText,
  CardHeader,
  Autocomplete,
  Switch,
  Collapse,
  Box,
} from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { getUserDefaults, saveDealerMistake, mistakesActions, getUserMistakes } from "store/slice/mistakesSlice";

const DealersMistakesEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(getUserDefaults());
  }, []);

  const dealers = useSelector((state) => state.mistakes.dealers);
  const mistakesStore = useSelector(state => state.mistakes);
  const mistakesListData = mistakesStore.mistakesListData;
  const groupedMistakes = mistakesStore.groupedMistakes;
  const resolution = mistakesStore.resolution;
  const studioGamesMap = useSelector((state) => state.mistakes.studioGamesMap || {});
  const studios = useSelector((state) => state.mistakes.studios || []);
  const reasons = useSelector((state) => state.mistakes.reasons);
  const saveStatus = useSelector((state) => state.mistakes.success);
  const saveError = useSelector((state) => state.mistakes.error);

  const formik = useFormik({
    initialValues: {
      dealer: "",
      type: "",
      studio: "",
      tableId: "",
      gameId: "",
      date: new Date(),
      time: new Date(),
      note: "",
      image: null,
      mistake: "",
      resolution: "",
      reviewEnabled: false,
      reviewComment: "",
    },
    validationSchema: Yup.object({
      dealer: Yup.string().required("Dealer is required"),
      type: Yup.string().required("Type is required"),
      studio: Yup.string().when("type", {
        is: "mistake",
        then: (schema) => schema.required("Studio is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      tableId: Yup.string().when("type", {
        is: "mistake",
        then: (schema) => schema.required("Game is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      mistake: Yup.string().required("Mistake is required"),
      resolution: Yup.string().nullable().when("type", {
        is: (val) => val === "mistake",
        then: () => Yup.string().required("Resolution is required"),
        otherwise: () => Yup.string().nullable(),
      }),
      gameId: Yup.number().typeError("Must be a number").when("type", {
        is: "mistake",
        then: (schema) => Yup.string().required("Game ID is required"),
        otherwise: (schema) => Yup.string().nullable()
      }),
      time: Yup.date().when("type", {
        is: "mistake",
        then: (schema) => schema.required("Time is required"),
        otherwise: (schema) => schema.nullable(),
      }),
      date: Yup.date().required("Date is required"),
      note: Yup.string().max(256),
      image: Yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
    
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
    
        if (value instanceof Date) {
          formData.append(key, value.toISOString());
        }
        else if (value instanceof File) {
          formData.append(key, value);
        }
        else if (typeof value === 'boolean') {
          formData.append(key, value ? 'true' : 'false');
        }
        else {
          formData.append(key, value);
        }
      });
    
      await dispatch(saveDealerMistake(formData));
    },
  });

  useEffect(() => {
    if (saveStatus?.status) {
      formik.resetForm();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      const timer = setTimeout(() => {
        dispatch(mistakesActions.reset());
        dispatch(getUserMistakes({}));
        navigate("/dealersMistakes");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [saveStatus?.status, dispatch, navigate]);

  const selectedStudioGames = studioGamesMap[formik.values.studio] || [];

  return (
    <DashboardLayout>
      <Grid container justifyContent="center" alignItems="flex-start">
        <Grid item size={{ xs: 12, md: 8 }}>
          <MDBox p={3} sx={{ border: "1px solid #e0e0e0", borderRadius: "12px" }}>
            <CardHeader subheader="Submit dealer mistake" title="Dealer mistake" />

            {saveStatus?.status && (
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                ✅ Dealer mistake submitted successfully!
              </Typography>
            )}
            {saveError?.status && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                ❌ {saveError.message || "An error occurred. Please try again."}
              </Typography>
            )}

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                {/* Dealer Dropdown */}
                <Grid item size={{ xs: 12 }}>
                  <FormControl fullWidth error={formik.touched.dealer && Boolean(formik.errors.dealer)}>

                    <Autocomplete
                      options={dealers}
                      getOptionLabel={(option) => option.name}
                      value={dealers.find((dealer) => dealer.id === formik.values.dealer) || null}
                      onChange={(event, value) => {
                        formik.setFieldValue("dealer", value ? value.id : "");
                      }}
                      onBlur={formik.handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Dealer"
                          name="dealer"
                          error={formik.touched.dealer && Boolean(formik.errors.dealer)}
                          helperText={formik.touched.dealer && formik.errors.dealer}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Date Picker */}
                <Grid item size={{ xs: 12 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd.MM.yyyy"
                      label="Date"
                      value={formik.values.date}
                      onChange={(date) => formik.setFieldValue("date", date)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={formik.touched.date && Boolean(formik.errors.date)}
                          helperText={formik.touched.date && formik.errors.date}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Type Dropdown */}
                <Grid item size={{ xs: 12 }}>
                  <FormControl fullWidth error={formik.touched.type && Boolean(formik.errors.type)}>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                      labelId="type-label"
                      id="type"
                      name="type"
                      value={formik.values.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        formik.setFieldValue("type", newType);
                        
                        formik.setFieldValue("mistake", "");
                        formik.setFieldValue("studio", "");
                        formik.setFieldValue("tableId", "");
                        formik.setFieldValue("gameId", "");
                        formik.setFieldValue("resolution", "");
                      }}
                      onBlur={formik.handleBlur}
                      label="Type"
                    >
                      {Object.entries(mistakesListData).map(([key, value]) => (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formik.touched.type && formik.errors.type}</FormHelperText>
                  </FormControl>
                </Grid>

                {formik.values.type === 'mistake' && (
                  <>
                    <Grid item size={{ xs: 12 }}>
                      <FormControl fullWidth error={formik.touched.studio && Boolean(formik.errors.studio)}>
                        <InputLabel id="studio-label">Studio</InputLabel>
                        <Select
                          labelId="studio-label"
                          id="studio"
                          name="studio"
                          value={formik.values.studio}
                          onChange={(e) => {
                            formik.handleChange(e);
                            formik.setFieldValue("tableId", ""); 
                          }}
                          onBlur={formik.handleBlur}
                          label="Studio"
                        >
                          {studios.map((studio) => (
                            <MenuItem key={studio.id} value={studio.id}>
                              {studio.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formik.touched.studio && formik.errors.studio}</FormHelperText>
                      </FormControl>
                    </Grid>

                    {formik.values.studio && (
                      <Grid item size={{ xs: 12 }}>
                        <FormControl fullWidth error={formik.touched.tableId && Boolean(formik.errors.tableId)}>
                          <InputLabel id="game-label">Table</InputLabel>
                          <Select
                            labelId="game-label"
                            id="tableId"
                            name="tableId"
                            value={formik.values.tableId}
                            label="Game"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            {selectedStudioGames.map((game) => (
                              <MenuItem key={game.id} value={game.id}>
                                {game.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{formik.touched.tableId && formik.errors.tableId}</FormHelperText>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Time Picker */}
                    <Grid item size={{ xs: 12 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        label="Time of Mistake"
                        value={formik.values.time}
                        onChange={(newValue) => formik.setFieldValue("time", newValue)}
                        ampm={false}
                        slotProps={{
                          textField: {
                            error: formik.touched.time && Boolean(formik.errors.time),
                            helperText: formik.touched.time && formik.errors.time,
                          },
                          popper: {
                            sx: {
                              "& .MuiPaper-root": {
                                backgroundColor: "#ffffff !important",
                              },
                              "& .Mui-selected": {
                                backgroundColor: "#1976d2 !important",
                                color: "#ffffff !important",
                                color: "white"
                              },
                              "& .MuiMultiSectionDigitalClockSection-item": {
                                display: "flex !important",
                                justifyContent: "flex-start !important",
                          
                              }
                            }
                          }
                        }}
                      />
                      </LocalizationProvider>
                    </Grid>

                    {/* Mistake */}
                    <Grid item size={{ xs: 12 }}>
                      <FormControl fullWidth error={formik.touched.mistake && Boolean(formik.errors.mistake)}>
                        <InputLabel id="game-label">Mistake</InputLabel>
                        <Select
                          key={`mistake-${formik.values.type}`}
                          labelId="game-label-mistake"
                          id="mistake"
                          name="mistake"
                          value={formik.values.mistake}
                          label="Mistake"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          {groupedMistakes[formik.values.type].map((mistake) => (
                            <MenuItem key={mistake.key} value={mistake.key}>
                              {mistake.name} 
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formik.touched.mistake && formik.errors.mistake}</FormHelperText>
                      </FormControl>
                    </Grid>
                    
                    {/* Resolution */}
                    <Grid item size={{ xs: 12 }}>
                      <FormControl fullWidth error={formik.touched.resolution && Boolean(formik.errors.resolution)}>
                        <InputLabel id="game-label">Resolution</InputLabel>
                        <Select
                          labelId="game-label"
                          id="resolution"
                          name="resolution"
                          value={formik.values.resolution}
                          label="Resolution"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          {Object.entries(resolution).map(([key, value]) => (
                            <MenuItem key={key} value={key}>
                              {value} 
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formik.touched.resolution && formik.errors.resolution}</FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* Game ID Number Field */}
                    <Grid item size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        type="text"
                        label="Game ID"
                        name="gameId"
                        value={formik.values.gameId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gameId && Boolean(formik.errors.gameId)}
                        helperText={formik.touched.gameId && formik.errors.gameId}
                      />
                    </Grid>
                  </>
                )}

                {formik.values.type === 'late' && (
                  <>
                    {/* Mistake */}
                    <Grid item size={{ xs: 12 }}>
                      <FormControl fullWidth error={formik.touched.mistake && Boolean(formik.errors.mistake)}>
                        <InputLabel id="game-label">Mistake</InputLabel>
                        <Select
                          key={`mistake-late-${formik.values.type}`}
                          labelId="game-mistake-late"
                          id="mistakeLate"
                          name="mistake"
                          value={formik.values.mistake}
                          label="Mistake"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          {groupedMistakes[formik.values.type].map((mistake) => (
                            <MenuItem key={mistake.key} value={mistake.key}>
                              {mistake.name} 
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>{formik.touched.mistake && formik.errors.mistake}</FormHelperText>
                      </FormControl>
                    </Grid>
                  </>
                )}
                
                {formik.values.type === 'other' && (
                       <>
                       {/* Mistake */}
                       <Grid item size={{ xs: 12 }}>
                         <FormControl fullWidth error={formik.touched.mistake && Boolean(formik.errors.mistake)}>
                           <InputLabel id="game-label">Mistake</InputLabel>
                           <Select
                             key={`mistake-other-${formik.values.type}`}
                             labelId="game-mistake-other"
                             id="mistakeOther"
                             name="mistake"
                             value={formik.values.mistake}
                             label="Mistake"
                             onChange={formik.handleChange}
                             onBlur={formik.handleBlur}
                           >
                             {groupedMistakes[formik.values.type].map((mistake) => (
                               <MenuItem key={mistake.key} value={mistake.key}>
                                 {mistake.name} 
                               </MenuItem>
                             ))}
                           </Select>
                           <FormHelperText>{formik.touched.mistake && formik.errors.mistake}</FormHelperText>
                         </FormControl>
                       </Grid>
                     </>
                )}

                {/* Note */}
                <Grid item size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Note"
                    name="note"
                    value={formik.values.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.note && Boolean(formik.errors.note)}
                    helperText={formik.touched.note && formik.errors.note}
                  />
                </Grid>

                {/* Image Upload */}
                <Grid item size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Image (optional)</Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      borderStyle: "dashed",
                      borderColor: "#bdbdbd",
                      backgroundColor: "#fafafa",
                      color: "#424242",
                      textAlign: "left",
                      padding: "12px 16px",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    {formik.values.image ? formik.values.image.name : "Click to upload image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        formik.setFieldValue("image", file);
                      }}
                    />
                  </Button>
                  {formik.values.image && (
                    <img
                      src={URL.createObjectURL(formik.values.image)}
                      alt="preview"
                      style={{
                        marginTop: 12,
                        width: "100%",
                        maxHeight: 180,
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #ccc",
                      }}
                    />
                  )}
                </Grid>

                {/* Mistake Review */}
                <Grid item size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      border: "1.5px solid",
                      borderColor: formik.values.reviewEnabled ? "info.main" : "#e0e0e0",
                      backgroundColor: formik.values.reviewEnabled ? "rgba(26,115,232,0.04)" : "#fafafa",
                      transition: "all 0.25s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => formik.setFieldValue("reviewEnabled", !formik.values.reviewEnabled)}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight="600" color={formik.values.reviewEnabled ? "info.main" : "text.primary"}>
                        Mistake Review
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add a review comment for this mistake
                      </Typography>
                    </Box>
                    <Switch
                      checked={formik.values.reviewEnabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        formik.setFieldValue("reviewEnabled", e.target.checked);
                        if (!e.target.checked) formik.setFieldValue("reviewComment", "");
                      }}
                      color="info"
                    />
                  </Box>

                  <Collapse in={formik.values.reviewEnabled} timeout={300}>
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: "10px",
                        backgroundColor: "rgba(25,118,210,0.04)",
                        border: "1px solid rgba(25,118,210,0.15)",
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        label="Review Comment"
                        name="reviewComment"
                        placeholder="Describe the review findings or feedback..."
                        value={formik.values.reviewComment}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.reviewComment && Boolean(formik.errors.reviewComment)}
                        helperText={formik.touched.reviewComment && formik.errors.reviewComment}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Box>
                  </Collapse>
                </Grid>

                {/* Submit Button */}
                <Grid item size={{ xs: 12 }}>
                  <Button type="submit" variant="contained" fullWidth size="large">
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default DealersMistakesEdit;
